import { useState, useEffect, useRef } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { companyService } from "@/api/services"
import type { CompanyResponseDto } from "@/api/types/company.types"
import Sidebar from "@/components/Sidebar"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Loader2, X, User, ExternalLink } from "lucide-react"

export default function CompaniesListPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [companies, setCompanies] = useState<CompanyResponseDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  // Filter states
  const [search, setSearch] = useState("")
  
  // Track if we've loaded initially
  const hasLoadedRef = useRef(false)

  // Debounce function
  const useDebounce = (value: string, delay: number) => {
    const [debouncedValue, setDebouncedValue] = useState(value)

    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value)
      }, delay)

      return () => {
        clearTimeout(handler)
      }
    }, [value, delay])

    return debouncedValue
  }

  // Debounced search value
  const debouncedSearch = useDebounce(search, 500)

  // Track previous debounced search to avoid unnecessary updates
  const prevDebouncedSearch = useRef<string | undefined>(undefined)

  // Initial load on mount
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadCompanies()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Reload when navigating back to this page (location change)
  useEffect(() => {
    if (hasLoadedRef.current && location.pathname === "/companies") {
      // Small delay to ensure we're fully on the page
      const timer = setTimeout(() => {
        loadCompanies()
      }, 100)
      return () => clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  // Auto-apply filters when search changes
  useEffect(() => {
    const newSearch = debouncedSearch || undefined
    
    // Skip if search hasn't actually changed
    if (prevDebouncedSearch.current === newSearch) {
      return
    }
    
    prevDebouncedSearch.current = newSearch
    loadCompanies()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])

  const loadCompanies = async () => {
    setLoading(true)
    setError("")
    try {
      const params = debouncedSearch ? { search: debouncedSearch } : undefined
      console.log("[CompaniesListPage] Loading companies with params:", params)
      const response = await companyService.getCompanies(params)
      console.log("[CompaniesListPage] Received companies:", response?.length || 0)
      
      // Ensure response is an array
      if (Array.isArray(response)) {
        setCompanies(response)
      } else {
        console.warn("[CompaniesListPage] Unexpected response format:", response)
        setCompanies([])
        setError("Unexpected response format from server")
      }
    } catch (err: any) {
      console.error("[CompaniesListPage] Error loading companies:", err)
      const errorMessage = err.response?.data?.message || err.message || "Failed to load companies"
      setError(errorMessage)
      setCompanies([])
      
      // If it's an auth error, the interceptor should handle redirect
      // But we still want to show the error state instead of black screen
      if (err.response?.status === 401 || err.response?.status === 403) {
        // Auth errors are handled by interceptor, but we should still show something
        setError("Authentication error. Please try refreshing the page.")
      }
    } finally {
      setLoading(false)
    }
  }

  const handleCompanyClick = (company: CompanyResponseDto) => {
    navigate(`/companies/${company.slug}`, {
      state: { ownerUserId: company.ownerUserId },
    })
  }

  const handleViewOwnerUser = (e: React.MouseEvent, ownerUserId: string) => {
    e.stopPropagation() // Prevent row click
    navigate(`/users/${ownerUserId}`)
  }

  const handleClearFilters = () => {
    setSearch("")
  }

  const hasActiveFilters = search !== ""

  return (
    <div className="flex">
      <Sidebar />

      <main className="flex-1 ml-64 p-8 bg-background min-h-screen">
        <div className="max-w-7xl">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Companies</h1>
              <p className="text-muted-foreground">View and manage companies in the system</p>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 mb-6">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Filters */}
          <Card className="p-6 mb-6">
            <div className="flex gap-4">
              <div className="flex-1 space-y-2">
                <Label htmlFor="search">Search</Label>
                <div className="relative">
                  <Input
                    id="search"
                    placeholder="Search by company name"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pr-8"
                  />
                  {search && (
                    <button
                      type="button"
                      onClick={handleClearFilters}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      title="Clear search"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {search && (
                  <p className="text-xs text-muted-foreground">
                    Searching automatically as you type...
                  </p>
                )}
              </div>
            </div>
          </Card>

          {/* Companies Table */}
          <Card>
            {loading ? (
              <div className="p-8 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : !companies || !Array.isArray(companies) || companies.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <p className="mb-2">No companies found</p>
                {hasActiveFilters && (
                  <p className="text-xs">Try clearing your filters to see all companies</p>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto px-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Slug</TableHead>
                      <TableHead>City</TableHead>
                      <TableHead>Contact Email</TableHead>
                      <TableHead>Contact Phone</TableHead>
                      <TableHead>Verified</TableHead>
                      <TableHead>Owner User</TableHead>
                      <TableHead>Created At</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {companies && Array.isArray(companies) && companies.map((company) => (
                      <TableRow
                        key={company.id}
                        className="cursor-pointer hover:bg-accent/50 transition-colors"
                        onClick={() => handleCompanyClick(company)}
                      >
                        <TableCell className="font-medium">{company.name}</TableCell>
                        <TableCell>
                          <code className="text-xs bg-muted px-2 py-1 rounded">
                            {company.slug}
                          </code>
                        </TableCell>
                        <TableCell>{company.headquartersCity || "N/A"}</TableCell>
                        <TableCell>{company.publicContactEmail || "N/A"}</TableCell>
                        <TableCell>{company.publicContactPhone || "N/A"}</TableCell>
                        <TableCell>
                          <Badge variant={company.verified ? "default" : "secondary"}>
                            {company.verified ? "Verified" : "Not Verified"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {company.ownerUserId ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => handleViewOwnerUser(e, company.ownerUserId)}
                              className="h-8 text-xs"
                            >
                              <User className="mr-1 h-3 w-3" />
                              View User
                              <ExternalLink className="ml-1 h-3 w-3" />
                            </Button>
                          ) : (
                            <span className="text-muted-foreground text-sm">N/A</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {new Date(company.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="text-muted-foreground text-sm">Click to view</span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>
        </div>
      </main>
    </div>
  )
}


